import {isRepository} from '../utils/copilot-chat-helpers'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {useChatState} from '../utils/CopilotChatContext'
import {useSelectedCopilotSpaceId} from './use-selected-custom-copilot-id'

export type SupportedAttachmentType =
  | 'repositories'
  | 'knowledge-bases'
  | 'upload'
  | 'agents'
  | 'copilot-spaces'
  | 'plugin-attachment-contribution'
export type SupportedReferenceType = 'files' | 'folders' | 'symbols'

/**
 * Generates a label for the multi-file-picker menu item based on which reference types are supported.
 */
export function getReferenceTypeLabel(supportedReferenceTypes: SupportedReferenceType[]): string {
  const includeFiles = supportedReferenceTypes.includes('files')
  const includeFolders = supportedReferenceTypes.includes('folders')
  const includeSymbols = supportedReferenceTypes.includes('symbols')

  if (includeFiles && includeFolders && includeSymbols) return 'Files, folders, and symbols'
  if (includeFiles && includeFolders) return 'Files and folders'
  if (includeFiles && includeSymbols) return 'Files and symbols'
  if (includeFolders && includeSymbols) return 'Folders and symbols'
  if (includeFiles) return 'Files'
  if (includeFolders) return 'Folders'
  if (includeSymbols) return 'Symbols'
  return 'References'
}

/**
 * Returns the supported attachment types and reference types based on the current state of the chat.
 * Reference types are specifically used in the ReferencesSelectPanel component.
 */
export function useSupportedAttachmentTypes(): {
  supportedAttachmentTypes: SupportedAttachmentType[]
  supportedReferenceTypes: SupportedReferenceType[]
} {
  const state = useChatState()
  const isCopilotSpaceAttachments = useSelectedCopilotSpaceId() !== null

  const supportedAttachmentTypes: SupportedAttachmentType[] = ['upload']

  const shouldShowKnowledge =
    state.renderKnowledgeBases && ((state.currentTopic && isRepository(state.currentTopic)) || !state.currentTopic)
  const knowledgeBasesDisabled = copilotFeatureFlags.knowledgeBasesDisabled

  if (!isCopilotSpaceAttachments) {
    supportedAttachmentTypes.push('repositories')
  }

  if (shouldShowKnowledge && !isCopilotSpaceAttachments && !knowledgeBasesDisabled) {
    supportedAttachmentTypes.push('knowledge-bases')
  }

  if (!isCopilotSpaceAttachments && copilotFeatureFlags.spacesAsAttachmentsEnabled) {
    supportedAttachmentTypes.push('copilot-spaces')
  }

  if (!isCopilotSpaceAttachments) {
    supportedAttachmentTypes.push('agents')
  }

  if (!isCopilotSpaceAttachments) {
    supportedAttachmentTypes.push('plugin-attachment-contribution')
  }

  const supportedReferenceTypes: SupportedReferenceType[] = isCopilotSpaceAttachments ? ['files'] : ['files', 'folders']
  return {supportedAttachmentTypes, supportedReferenceTypes}
}
